/**
 * O que a simulação MEDIU, e quando.
 *
 * Existe como módulo próprio por um defeito concreto: `ResultadoFisica.impactos`
 * cobre o evento inteiro, e quem desenha recebia a lista inteira. As regiões
 * corporais de atenção e a lista de impactos acendiam com a régua de tempo em
 * zero — o boneco "machucava" antes de o carro encostar em coisa alguma.
 *
 * A correção não é filtrar em cada consumidor: é o instante virar PARÂMETRO
 * OBRIGATÓRIO de quem traduz medição em leitura. Assim esquecer de filtrar deixa
 * de ser um defeito visual e passa a ser erro de compilação — o mesmo raciocínio
 * que o projeto já aplica em `aprisionamentos`, filtrado por `tempo` nos dois
 * lugares onde é lido.
 */

/** Um contato medido pela física, na régua 0..1 da timeline. */
export interface ImpactoMedido {
  /** id do corpo que bateu; nos segmentos do ragdoll vem como `ocupante:segmento` */
  id: string
  nomeCorpo: string
  /** nome do que ele encontrou: uma peça do habitáculo ou uma pessoa */
  alvo: string
  /** instante normalizado 0..1, na mesma régua da timeline */
  tempo: number
  /** velocidade relativa ao veículo imediatamente ANTES de ser detido, em km/h */
  velocidadeRelativaKmh: number
}

/**
 * Os impactos que já aconteceram até o instante.
 *
 * Inclusivo no limite: um impacto registrado exatamente em `tempo` já é visível,
 * senão a leitura piscaria um quadro depois do contato que a descreve.
 */
export function impactosAte<T extends { tempo: number }>(
  impactos: readonly T[],
  tempo: number,
): T[] {
  return impactos.filter((impacto) => impacto.tempo <= tempo)
}

/**
 * O impacto pertence a este ocupante.
 *
 * A pelve preserva o id público do ocupante e os demais segmentos do ragdoll
 * ganham `ocupante:segmento` (`idSegmentoRagdoll`), então os dois casos contam.
 * Estava escrito duas vezes — em `atencaoCorporal` e em `sequenciaImpactos` —
 * e uma terceira cópia acabaria discordando das outras.
 */
export const pertenceAoOcupante = (id: string, idOcupante: string): boolean =>
  id === idOcupante || id.startsWith(`${idOcupante}:`)
